import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../scss/Home.scss';
import '../scss/Changelog.scss';
import { StaticHeader, StaticFooter } from '../components';

const Changelog = () => {
  const [selectedVersion, setSelectedVersion] = useState('v0.4');

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const versions = [
    {
      version: 'v0.4',
      date: 'August 2025',
      title: 'White Label & Performance',
      badge: 'Latest',
      badgeColor: 'primary',
      features: [
        {
          category: '🎨 White Label',
          items: [
            'Custom Branding - Add your logo and choose your brand colors',
            'Custom Domain - Use your own domain to create a professional space',
            'Brand Identity - Make Zoobbe an extension of your company\'s identity',
            'Professional Workspace - Create a space that feels 100% yours',
            'No More Blending In - Stand out with your unique brand'
          ]
        },
        {
          category: '⚡ Performance Improvements',
          items: [
            '10x Faster - Significantly improved speed and responsiveness',
            'Lighter Build - Optimized bundle size for faster loading',
            'Smoother Experience - Enhanced UI interactions and transitions',
            'Better Resource Management - Reduced memory footprint',
            'Way More Responsive - Instant feedback on every action'
          ]
        },
        {
          category: '🔧 Technical Enhancements',
          items: [
            'Experimental Features - Early access to cutting-edge functionality (v0.4 experimental)',
            'Improved Stability - Bug fixes and performance optimizations',
            'Better Mobile Support - Enhanced responsive design',
            'Modern Architecture - Cleaner, more maintainable codebase'
          ]
        }
      ]
    },
    {
      version: 'v0.03',
      date: 'March 2025',
      title: 'The AI Revolution',
      features: [
        {
          category: '🤖 AI-Powered Features',
          items: [
            'AI Chatbot Assistant - Get instant help, suggestions, and insights',
            'Smart Task Recommendations - AI suggests next actions based on your workflow',
            'Intelligent Search - Natural language search across all your content',
            'Auto-categorization - AI automatically tags and organizes tasks',
            'Smart Notifications - Context-aware alerts that matter'
          ]
        },
        {
          category: '📊 Advanced Analytics',
          items: [
            'Team Performance Dashboard - Track productivity metrics in real-time',
            'Burndown Charts - Visualize project progress',
            'Time Tracking Analytics - Understand where time is spent',
            'Custom Reports - Generate insights tailored to your needs',
            'Export Analytics - Download reports in CSV/PDF formats'
          ]
        },
        {
          category: '🔐 Enhanced Authentication',
          items: [
            'Google OAuth Integration - Sign in with Google',
            'GitHub OAuth Support - Developer-friendly authentication',
            'Two-Factor Authentication - Extra security layer',
            'Session Management - Better control over active sessions',
            'Password Recovery Improvements - Streamlined reset process'
          ]
        },
        {
          category: '⚡ Performance & UX',
          items: [
            'Faster load times - 40% improvement in initial page load',
            'Optimized real-time sync - Smoother collaboration experience',
            'Enhanced mobile responsiveness - Better experience on all devices',
            'Improved error handling - Better user feedback',
            'Modular architecture - Cleaner, maintainable code'
          ]
        }
      ]
    },
    {
      version: 'v0.02',
      date: 'February 2025',
      title: 'Timer & Productivity',
      features: [
        {
          category: '⏱️ Timer Features',
          items: [
            'Built-in Timer - Track time spent on tasks',
            'Stopwatch Mode - Measure task duration accurately',
            'Countdown Timer - Set time limits for focused work',
            'Clean Interface - Minimal, distraction-free design',
            'Precise Tracking - Accurate to the second'
          ]
        },
        {
          category: '🎯 Productivity Enhancements',
          items: [
            'Task Time Estimates - Set expected duration for tasks',
            'Time Reports - See how time is distributed',
            'Focus Mode - Minimize distractions while working',
            'Regional Availability - Rolling out to selected regions'
          ]
        }
      ]
    },
    {
      version: 'v0.01',
      date: 'January 2025',
      title: 'Launch & Trello Import',
      badge: 'Initial Release',
      badgeColor: 'success',
      features: [
        {
          category: '🚀 Core Features',
          items: [
            'Real-time collaboration - Instant updates, no refresh needed',
            'Advanced task management - Organize, prioritize, and track tasks',
            'Drag & drop boards - Move tasks/lists smoothly',
            'Smart notifications - Browser, Slack, app, and email alerts',
            '@Mentions & tagging - Notify teammates instantly',
            'Online status - See who\'s online in real-time',
            'Custom labels & colors - Visually organize tasks',
            'Workspace & board roles - Set permissions for control',
            'File uploads - Attach images, documents, and files',
            'Global search - Find anything instantly',
            'Fast & lightweight - No lag, just productivity'
          ]
        },
        {
          category: '🔄 Trello Migration',
          items: [
            'One-click Trello import - Connect and import boards instantly',
            'Import boards, lists, cards - Complete data migration',
            'Choose which boards to import - Selective migration',
            'Preserve structure - Maintain your organization',
            'No exports needed - Direct connection to Trello'
          ]
        },
        {
          category: '🎉 Launch Offer',
          items: [
            'First 100 testers get lifetime free access',
            'Experimental features for early adopters',
            'Community-driven development',
            'Affordable & scalable pricing'
          ]
        }
      ]
    }
  ];

  const currentVersion = versions.find(v => v.version === selectedVersion);

  return (
    <div className="landing-page changelog-page">
      {/* Navigation */}
      <StaticHeader />

      {/* Hero Section */}
      <section className="changelog-hero">
        <div className="hero__container container">
          <h1 className="hero__title">
            <span className="hero__title-accent">Changelog</span>
          </h1>
          <p className="hero__subtitle">
            What's new in Zoobbe
          </p>
        </div>
      </section>

      {/* Changelog Content */}
      <section className="changelog-content">
        <div className="container">
          <div className="changelog-layout">
            {/* Version Sidebar */}
            <aside className="version-sidebar">
              <h3>Versions</h3>
              <div className="version-list">
                {versions.map((v) => (
                  <button
                    key={v.version}
                    className={`version-item ${selectedVersion === v.version ? 'active' : ''}`}
                    onClick={() => setSelectedVersion(v.version)}
                  >
                    <span className="version-number">{v.version}</span>
                    {v.badge && (
                      <span className={`version-badge ${v.badgeColor || 'primary'}`}>
                        {v.badge}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </aside>

            {/* Version Details */}
            <main className="version-details">
              {currentVersion && (
                <div className="version-card">
                  <div className="version-header">
                    <div className="version-title-group">
                      <h2>{currentVersion.title}</h2>
                      <p className="version-date-large">{currentVersion.version} · {currentVersion.date}</p>
                      {currentVersion.badge && (
                        <span className={`version-badge-large ${currentVersion.badgeColor || 'primary'}`}>
                          {currentVersion.badge}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="features-list">
                    {currentVersion.features.map((section, idx) => (
                      <div key={idx} className="feature-section">
                        <h3 className="feature-category">{section.category}</h3>
                        <ul className="feature-items">
                          {section.items.map((item, itemIdx) => (
                            <li key={itemIdx} className="feature-item">
                              <svg className="check-icon" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </main>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Experience the Latest Features?</h2>
            <p>Join thousands of teams already using Zoobbe to stay organized and productive.</p>
            <div className="cta-actions">
              <Link to="/signup" className="cta-button primary">Get Started Free</Link>
              <Link to="/about" className="cta-button secondary">Learn More</Link>
            </div>
            {/* <p className="cta-note">No credit card required • Free forever plan available</p> */}
          </div>
        </div>
      </section>

      {/* Footer */}
      <StaticFooter />
    </div>
  );
};

export default Changelog;

